'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { User, LogOut, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ICONS } from '@/lib/constants/castorama';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { useSession, signOut } from '@/lib/auth-client';
import { canAccessRoute, ROLE_LABELS, type AppRole } from '@/lib/roles';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface SidebarProps {
  isCollapsed?: boolean;
}

interface NavItem {
  href: string;
  label: string;
  icon: string;
  badge?: number;
}

const navItems: NavItem[] = [
  { href: '/dashboard', label: 'Tableau de bord', icon: '📊' },
  { href: '/assistant', label: 'Assistant N1', icon: '🤖' },
  { href: '/roi', label: 'Simulateur ROI', icon: '💶' },
  { href: '/tickets', label: 'Tickets', icon: '🎫' },
  { href: '/nouvel-appel', label: 'Traiter un appel', icon: '📞' },
  { href: '/portail', label: 'Portail collaborateur', icon: '💬' },
  { href: '/satisfaction-survey', label: 'Thermomètre satisfaction', icon: '🌡️' },
  { href: '/concentrix-nps', label: 'NPS Sondage Externe', icon: '📈' },
  { href: '/callback-promises', label: 'Promesses de rappel', icon: '📞' },
  { href: '/manager-escalations', label: 'Escalades responsable', icon: '🔺' },
  { href: '/agents', label: 'Agents', icon: '👥' },
  { href: '/nomenclature', label: 'Nomenclature produits', icon: '🗂️' },
  { href: '/knowledge-base', label: 'Base de Connaissances', icon: '📚' },
  { href: '/fine-tuning', label: 'Fine-Tuning', icon: '🎯' },
  { href: '/pipeline', label: 'Pipeline', icon: '⚡' },
  { href: '/calls', label: 'Appels', icon: ICONS.calls },
  { href: '/alerts', label: 'Alertes', icon: ICONS.alerts },
  { href: '/widgets', label: 'Widget Builder', icon: '🔧' },
  { href: '/mes-widgets', label: 'Mes Widgets', icon: '📦' },
  { href: '/marketplace', label: 'Marketplace', icon: '🛒' },
  { href: '/config', label: 'Configuration', icon: ICONS.settings },
  { href: '/admin', label: 'Administration', icon: '🔐' },
];

export function Sidebar({ isCollapsed = false }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: sessionData } = useSession();
  const [loggingOut, setLoggingOut] = useState(false);
  const BRANDS = [
    { key: 'casino', label: 'Groupe Casino', sub: 'DSI · toutes enseignes' },
    { key: 'monoprix', label: 'Monoprix', sub: 'Vue enseigne' },
    { key: 'monop', label: "Monop'", sub: 'Vue enseigne' },
    { key: 'naturalia', label: 'Naturalia', sub: 'Vue enseigne' },
    { key: 'franprix', label: 'Franprix', sub: 'Vue enseigne' },
    { key: 'dcf', label: 'Casino DCF', sub: 'Vue enseigne' },
    { key: 'sco', label: 'SCO Libre-service', sub: 'Vue enseigne' },
  ];
  const [brand, setBrand] = useState(BRANDS[0]);

  const userName = sessionData?.user?.name || 'Utilisateur';
  const userInitials = userName.slice(0, 2).toUpperCase();
  const userRole = (sessionData?.user as { role?: string } | undefined)?.role;
  const userRoleLabel = ROLE_LABELS[userRole as AppRole] || userRole || 'Utilisateur';

  const visibleNavItems = navItems.filter(item => canAccessRoute(userRole, item.href));

  async function handleLogout() {
    setLoggingOut(true);
    await signOut({
      fetchOptions: {
        onSuccess: () => {
          router.push('/login');
          router.refresh();
        },
      },
    });
    setLoggingOut(false);
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen border-r bg-card flex flex-col transition-all duration-300 z-50',
        isCollapsed ? 'w-14' : 'w-60',
      )}
    >
      {/* Logo ConvivIA + sélecteur multi-marques */}
      <div className="px-3 py-2 border-b">
        <div className={cn('flex items-center gap-2', isCollapsed ? 'justify-center' : 'justify-start')}>
          <img src="/convivia-logo.png" alt="ConvivIA" className="h-7 w-7 shrink-0 object-contain" />
          {!isCollapsed && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button id="brand-switcher" className="flex-1 flex items-center justify-between gap-1 rounded-md px-1.5 py-1 hover:bg-accent text-left">
                  <span className="leading-tight">
                    <span className="block text-sm font-bold text-primary">{brand.label}</span>
                    <span className="block text-[10px] text-muted-foreground">{brand.sub}</span>
                  </span>
                  <ChevronUp className="h-3 w-3 rotate-180 text-muted-foreground shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground">Vue par enseigne (multi-marques)</div>
                {BRANDS.map((b) => (
                  <DropdownMenuItem key={b.key} onClick={() => setBrand(b)} className="flex flex-col items-start gap-0">
                    <span className="text-sm font-medium">{b.label}</span>
                    <span className="text-[10px] text-muted-foreground">{b.sub}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        <div className="space-y-0.5">
          {visibleNavItems.map(item => {
            const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);

            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium transition-colors no-underline',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  isCollapsed && 'justify-center',
                )}
                title={isCollapsed ? item.label : undefined}
              >
                <span className="text-base w-5 text-center shrink-0">{item.icon}</span>
                {!isCollapsed && <span>{item.label}</span>}
                {!isCollapsed && item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-auto bg-destructive text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User Section */}
      <div className="border-t p-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full cursor-pointer">
            <div
              className={cn(
                'flex items-center gap-2 p-1.5 rounded-md hover:bg-accent transition-colors',
                isCollapsed && 'justify-center',
              )}
            >
              <div className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                {userInitials}
              </div>
              {!isCollapsed && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs font-medium truncate">{userName}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{userRoleLabel}</p>
                  </div>
                  <ChevronUp className="w-3 h-3 text-muted-foreground" />
                </>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" className="w-48">
            <div className="px-2 py-1">
              <p className="text-xs font-medium">{userName}</p>
              <p className="text-[10px] text-muted-foreground">{userRoleLabel}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="text-xs cursor-pointer">
              <Link href="/profile">
                <User className="w-3.5 h-3.5 mr-1.5" />
                Profil
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer text-xs">
              <ThemeSwitcher variant="menu" />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              disabled={loggingOut}
              variant="destructive"
              className="cursor-pointer text-xs"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" />
              {loggingOut ? 'Déconnexion...' : 'Se déconnecter'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

export default Sidebar;
